import type { ComponentType, ReactNode, SVGProps } from 'react';
import { NavLink } from 'react-router-dom';
import { clsx } from 'clsx';

type IconType = ComponentType<
  SVGProps<SVGSVGElement> & { width?: number; height?: number }
>;

type ItemProps = {
  title: string;
  Icon: IconType;
  to?: string;
  onClick?: () => void;
  indent?: number;
  /** Keeps "More" highlighted while any of its children are active. */
  forceActive?: boolean;
  trailing?: ReactNode;
};

const activeBorder = {
  borderLeft: '4px solid var(--color-sidebarItemTextSelected)',
  marginLeft: '-4px',
};

/**
 * Port of `sidebar/Item.tsx`: 15px icon + label on a 20px row, padding
 * 9px/10px/(19+indent)px, 15px medium text, and a 4px left accent when active.
 */
export function Item({
  title,
  Icon,
  to,
  onClick,
  indent = 0,
  forceActive = false,
  trailing,
}: ItemProps) {
  const padding = `9px 10px 9px ${19 + indent}px`;

  const inner = (
    <span
      className="flex w-full items-center"
      style={forceActive ? activeBorder : undefined}
    >
      <Icon width={15} height={15} />
      <span className="ml-2 flex-1 truncate text-left">{title}</span>
      {trailing}
    </span>
  );

  const base =
    'block h-[20px] shrink-0 cursor-pointer text-md font-medium no-underline text-sidebar-item-text hover:bg-sidebar-item-background-hover';

  if (to) {
    return (
      <NavLink
        to={to}
        style={{ padding }}
        className={({ isActive }) =>
          clsx(base, 'flex items-center', isActive && 'text-sidebar-item-text-selected')
        }
      >
        {({ isActive }) => (
          <span
            className="flex w-full items-center"
            style={isActive || forceActive ? activeBorder : undefined}
          >
            <Icon width={15} height={15} />
            <span className="ml-2 flex-1 truncate text-left">{title}</span>
            {trailing}
          </span>
        )}
      </NavLink>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      style={{ padding }}
      className={clsx(
        base,
        'w-full text-left',
        forceActive && 'text-sidebar-item-text-selected',
      )}
    >
      {inner}
    </button>
  );
}

export function SectionDivider({ marginTop = 15 }: { marginTop?: number }) {
  return (
    <div
      className="h-px shrink-0 bg-sidebar-item-background-hover"
      style={{ marginTop }}
    />
  );
}
